import React from 'react';
import { Card, Avatar, Typography, Space, Checkbox, Flex } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { TelegramAccountEntity } from '../../../../domain';
import { Pagination } from '../../../types/Pagination';
import SelectableList, { SelectableListItem } from '../../SelectableList/SelectableList.component';

const { Text } = Typography;

export interface TelegramAccountStepProps {
  telegramAccounts: TelegramAccountEntity[];
  isLoading: boolean;
  pagination: Pagination;
  selectedTelegramAccount: Record<string, boolean>;
  searchTerm: string;
  onTelegramAccountSelect: (accountId: string, selected: boolean) => void;
  onPageChange: (page: number, pageSize: number) => void;
  onSearchChange: (term: string) => void;
  onCreateTelegramAccount: () => void;
  multiSelect?: boolean;
}

const TelegramAccountStep: React.FC<TelegramAccountStepProps> = ({
  telegramAccounts,
  isLoading,
  pagination,
  selectedTelegramAccount,
  searchTerm,
  onTelegramAccountSelect,
  onPageChange,
  onSearchChange,
  onCreateTelegramAccount,
  multiSelect = false
}) => {
  const { t } = useTranslation();

  const renderTelegramAccountItem = (item: SelectableListItem, isSelected: boolean, onSelect: (selected: boolean) => void) => {
    const account = item as TelegramAccountEntity;
    return (
      <Card
        style={{
          cursor: 'pointer',
          border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9'
        }}
        onClick={() => onSelect(!isSelected)}
      >
        <Flex justify="space-between" align="center">
          <Space>
            <Avatar icon={<UserOutlined />} />
            <div>
              <Text strong>{account.phone}</Text>
              <br />
              <Text type="secondary">{t("tg-account-status-" + account.status.toLowerCase(), { ns: 'standard' })}</Text>
            </div>
          </Space>
          <Checkbox
            checked={isSelected}
            onChange={(e) => onSelect(e.target.checked)}
            style={{ marginInlineEnd: '8px' }}
          />
        </Flex>
      </Card>
    );
  };

  return (
    <SelectableList
      items={telegramAccounts}
      loading={isLoading}
      multiSelect={multiSelect}
      searchTerm={searchTerm}
      selectedItems={selectedTelegramAccount}
      pagination={{
        current: pagination.page,
        total: pagination.total,
        pageSize: pagination.perPage
      }}
      searchPlaceholder={t('search-telegram-accounts', { ns: 'standard' })}
      emptyStateTitle={t('no-telegram-accounts', { ns: 'labels' })}
      emptyStateDescription={t('no-telegram-accounts-description', { ns: 'standard' })}
      createButtonText={t('add-telegram-account', { ns: 'buttons' })}
      onSearchChange={onSearchChange}
      onItemSelect={onTelegramAccountSelect}
      onPageChange={onPageChange}
      onCreateClick={onCreateTelegramAccount}
      renderItem={renderTelegramAccountItem}
      getItemId={(account) => account.uuid}
    />
  );
};

export default TelegramAccountStep;
